import { GID_MASK } from '../utils/constants.js';

/**
 * Parse a TMJ (Tiled Map JSON) object into EditorState-compatible data.
 * Supports arbitrary layer counts, tileset configurations, tile animations,
 * and per-tile collision shapes.
 */
export function parseTMJ(tmj) {
  const mapWidth = tmj.width;
  const mapHeight = tmj.height;
  const tileWidth = tmj.tilewidth;
  const tileHeight = tmj.tileheight;

  // Parse layers (recursive for group layers)
  const layers = flattenLayers(tmj.layers || []);

  // Parse tilesets + tile-level data (animations, collisions)
  const tileAnimations = new Map();
  const tileCollisions = new Map();

  const tilesets = (tmj.tilesets || []).map(ts => {
    const parsed = {
      firstGid: ts.firstgid,
      name: ts.name || 'Untitled',
      image: ts.image || '',
      imageWidth: ts.imagewidth || 0,
      imageHeight: ts.imageheight || 0,
      tileWidth: ts.tilewidth || tileWidth,
      tileHeight: ts.tileheight || tileHeight,
      tileCount: ts.tilecount || 0,
      columns: ts.columns || 0,
      margin: ts.margin || 0,
      spacing: ts.spacing || 0,
    };

    // Parse per-tile data (animations and collision shapes)
    if (ts.tiles && Array.isArray(ts.tiles)) {
      for (const tile of ts.tiles) {
        const globalId = ts.firstgid + tile.id;

        // Animation frames
        if (tile.animation && Array.isArray(tile.animation)) {
          tileAnimations.set(globalId, tile.animation.map(f => ({
            tileId: f.tileid,
            duration: f.duration,
          })));
        }

        // Collision shapes (objectgroup)
        if (tile.objectgroup && tile.objectgroup.objects) {
          const shapes = [];
          for (const obj of tile.objectgroup.objects) {
            if (obj.polygon) {
              shapes.push({
                type: 'polygon',
                x: obj.x || 0,
                y: obj.y || 0,
                points: obj.polygon.map(p => ({ x: p.x, y: p.y })),
              });
            } else {
              // Rectangle (default object type in Tiled)
              shapes.push({
                type: 'rect',
                x: obj.x || 0,
                y: obj.y || 0,
                width: obj.width || 0,
                height: obj.height || 0,
              });
            }
          }
          if (shapes.length > 0) {
            tileCollisions.set(globalId, shapes);
          }
        }
      }
    }

    return parsed;
  });

  // Parse map properties
  const properties = parseProperties(tmj.properties);

  return {
    mapWidth,
    mapHeight,
    tileWidth,
    tileHeight,
    orientation: tmj.orientation || 'orthogonal',
    renderOrder: tmj.renderorder || 'right-down',
    layers,
    tilesets,
    properties,
    tileAnimations,
    tileCollisions,
  };
}

function flattenLayers(layerArray, depth = 0) {
  const result = [];
  for (const layer of layerArray) {
    if (layer.type === 'group') {
      result.push(...flattenLayers(layer.layers || [], depth + 1));
    } else if (layer.type === 'tilelayer') {
      result.push({
        name: layer.name || `Layer ${result.length}`,
        type: 'tilelayer',
        data: new Uint32Array(layer.data || []),
        visible: layer.visible !== false,
        locked: false,
        opacity: layer.opacity ?? 1,
        properties: parseProperties(layer.properties),
        offsetX: layer.offsetx || 0,
        offsetY: layer.offsety || 0,
      });
    } else if (layer.type === 'objectgroup') {
      result.push({
        name: layer.name || `Objects ${result.length}`,
        type: 'objectgroup',
        objects: (layer.objects || []).map(obj => ({
          id: obj.id,
          name: obj.name || '',
          type: obj.type || '',
          x: obj.x,
          y: obj.y,
          width: obj.width || 0,
          height: obj.height || 0,
          rotation: obj.rotation || 0,
          visible: obj.visible !== false,
          properties: parseProperties(obj.properties),
          polygon: obj.polygon || null,
          polyline: obj.polyline || null,
          point: obj.point || false,
          gid: obj.gid || 0,
        })),
        visible: layer.visible !== false,
        locked: false,
        opacity: layer.opacity ?? 1,
        properties: parseProperties(layer.properties),
      });
    }
  }
  return result;
}

function parseProperties(props) {
  if (!props) return {};
  if (Array.isArray(props)) {
    const result = {};
    for (const p of props) {
      result[p.name] = { type: p.type || 'string', value: p.value };
    }
    return result;
  }
  return props;
}
